import {Component, OnDestroy, OnInit} from "@angular/core";
import {finalize, noop, Subject, take, takeUntil} from "rxjs";
import {NotificationService, Notification} from "@pb-labs/notification";
import {UserSettings} from "@app/services/sftp-api/sftp-api.service";
import {BrowseContentMode, BrowseService} from "@app/modules/browse/services/browse.service";
import {NodeTypes, TreeNode} from "@app/modules/models";
import {DeleteVerificationModalService} from "@app/modules/browse/components/delete-verification-modal/delete-verification-modal.service";
import {FileUploadModalService} from "@app/modules/browse/components/file-upload-modal/file-upload-modal.service";
import {FolderAccessRight, UserAccessRight, userAccessRights, UserFolders} from "@app/modules/browse/models";
import {SFTPConnectionService} from "@app/services/sftp-connection.service";

export const BATCH_FILE_NAME = "lcd_data_exchange_files_batch.zip";

@Component({
  selector: "app-browse",
  templateUrl: "./browse.component.html",
  styleUrls: ["./browse.component.scss"],
})
export class BrowseComponent implements OnInit, OnDestroy {
  userSettings: UserSettings;
  userAccessRight: UserAccessRight = null;
  parentNodes: TreeNode[];
  selectedFolder: TreeNode;
  files: TreeNode[] = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  browseSidebarOpen = true;
  userCornerPopupOpen = false;
  parentNodesLoading = false;
  loadingChildNodes = false;
  browseContentMode: BrowseContentMode = BrowseContentMode.BrowseTable;
  readonly browseContentModes = BrowseContentMode;
  downloadFiles = new Set<string>();

  private _destroy$: Subject<void> = new Subject<void>();

  constructor(
    private sftpConnectionService: SFTPConnectionService,
    private browseService: BrowseService,
    private deleteVerificationModalService: DeleteVerificationModalService,
    private fileUploadModalService: FileUploadModalService,
    private notificationService: NotificationService
  ) {}

  get locationLabel(): string {
    return this.browseContentMode === this.browseContentModes.UserSettings ? "Settings" : this.selectedFolder?.label;
  }

  ngOnInit(): void {
    this.sftpConnectionService.initialize();
    this.browseService.initialize();
    this.fileUploadModalService.initialize();

    this.browseService
      .getUserSettings()
      .pipe(takeUntil(this._destroy$))
      .subscribe((userSettings) => {
        this.userAccessRight = userAccessRights[userSettings.access_rights];
        this.userSettings = userSettings;
      });

    this.browseService.browseContentMode$.pipe(takeUntil(this._destroy$)).subscribe((mode: BrowseContentMode) => {
      this.browseContentMode = mode;
    });

    this.loadParentNodes();
  }

  ngOnDestroy(): void {
    this.sftpConnectionService.destroy();
    this.browseService.destroy();
    this.fileUploadModalService.destroy();

    this._destroy$.next();
    this._destroy$.complete();
  }

  get fileDownloading(): boolean {
    return this.browseService.fileDownloading;
  }

  get fileDeleting(): boolean {
    return this.browseService.fileDeleting;
  }

  isUserHasReadOnlyAccessToFolder(): boolean {
    switch (this.selectedFolder?.label) {
      case UserFolders.INBOX:
        return this.userAccessRight[UserFolders.INBOX] === FolderAccessRight.READ;

      case UserFolders.OUTBOX:
        return this.userAccessRight[UserFolders.OUTBOX] === FolderAccessRight.READ;

      default:
        return true;
    }
  }

  onCloseIcon(): void {
    this.browseSidebarOpen = false;
  }

  browseSidebarOpenChange(value: boolean): void {
    this.browseSidebarOpen = value;
  }

  onUserSettingsSelect(): void {
    this.browseService.updateBrowseContentMode(this.browseContentModes.UserSettings);
  }

  onFolderSelect(node: TreeNode): void {
    if (node) {
      if (this.browseContentMode !== this.browseContentModes.BrowseTable) {
        this.browseService.updateBrowseContentMode(this.browseContentModes.BrowseTable);
      }

      this.loadingChildNodes = true;
      this.selectedFolder = node;

      this.browseService
        .onFolderSelect(node)
        .pipe(
          takeUntil(this._destroy$),
          finalize(() => {
            this.loadingChildNodes = false;
          })
        )
        .subscribe((data: TreeNode[]) => {
          this.files = data;
        });
    }
  }

  onUploadFile(): void {
    this.fileUploadModalService
      .startDialog$(this.selectedFolder)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.onFolderSelect(this.selectedFolder);
        },
        error: noop,
      });
  }

  onDownloadFile(filename: string): void {
    const filePath = `${this.selectedFolder.data}/${filename}`;
    this.addDownloadFile(filename);

    this.browseService
      .downloadFile(filePath)
      .pipe(
        finalize(() => {
          this.removeDownloadFile(filename);
        })
      )
      .subscribe({
        next: (data) => {
          const downloadURL = URL.createObjectURL(new Blob([data]));
          const link = document.createElement("a");
          link.href = downloadURL;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(downloadURL);
        },
        error: () => {
          this.notificationService.showError(
            new Notification({
              message: "Downloading file failed.",
              details: `File name: ${filename}`,
              timeout: 5000,
            })
          );
        },
      });
  }

  onFilesBatchDownload(filenames: string[]): void {
    filenames.forEach(this.addDownloadFile.bind(this));

    const filePaths = filenames.map((filename) => `${this.selectedFolder.data}/${filename}`);

    this.browseService
      .downloadFilesBatch(filePaths)
      .pipe(
        finalize(() => {
          filenames.forEach(this.removeDownloadFile.bind(this));
        })
      )
      .subscribe({
        next: (data) => {
          const downloadURL = URL.createObjectURL(new Blob([data]));
          const link = document.createElement("a");
          link.href = downloadURL;
          link.download = BATCH_FILE_NAME;
          link.click();
        },
        error: () => {
          this.notificationService.showError(
            new Notification({message: "Downloading files batch failed.", timeout: 5000})
          );
        },
      });
  }

  onDeleteFile(filename: string): void {
    this.deleteVerificationModalService
      .checkWarning(`Are you sure you want to delete the ${filename}?`)
      .pipe(take(1), takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          const filePath = `${this.selectedFolder.data}/${filename}`;

          const input = {
            node_name: filePath,
            node_type: NodeTypes.File,
          };

          this.browseService
            .deleteNode(input)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
              next: () => {
                this.notificationService.showSuccess(
                  new Notification({message: "File deleted successfully.", timeout: 2000})
                );

                this.onFolderSelect(this.selectedFolder);
              },
              error: () => {
                this.notificationService.showError(new Notification({message: "Deleting file failed.", timeout: 5000}));
              },
            });
        },
        error: noop,
      });
  }

  private addDownloadFile(filename: string): void {
    this.downloadFiles.add(filename);
  }

  private removeDownloadFile(filename: string): void {
    this.downloadFiles.delete(filename);
  }

  private loadParentNodes(): void {
    const input = {
      node_name: "/",
      node_type: NodeTypes.Folder,
    };

    this.parentNodesLoading = true;

    this.browseService
      .getChildNodes(input)
      .pipe(
        takeUntil(this._destroy$),
        finalize(() => {
          this.parentNodesLoading = false;
        })
      )
      .subscribe((data: TreeNode[]) => {
        this.parentNodes = data as TreeNode[];
        this.onFolderSelect(this.parentNodes.find((item) => item.label === UserFolders.INBOX));
      });
  }
}
